// Service.js
export class Service {
  constructor(data = {}) {
    this.id = data.id || this.generateId();
    this.name = data.name || '';
    this.description = data.description || '';
    this.duration = data.duration || 0;
    this.price = data.price || 0;
    this.color = data.color || '#F8BBD9';
    this.category = data.category || '';
    this.is_active = data.is_active !== undefined ? data.is_active : true;
    this.is_visible_to_customers = data.is_visible_to_customers !== undefined ? data.is_visible_to_customers : true;
    this.show_price_to_customers = data.show_price_to_customers !== undefined ? data.show_price_to_customers : true;
    this.created_at = data.created_at || new Date().toISOString();
    this.updated_at = data.updated_at || new Date().toISOString();
  }

  generateId() {
    return 'service_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Validation methods
  static validate(data) {
    const errors = [];

    if (!data.name || data.name.trim().length === 0) {
      errors.push('יש להזין את שם הטיפול');
    }

    if (!data.duration || data.duration <= 0) {
      errors.push('משך הטיפול חייב להיות גדול מאפס');
    }

    if (data.price === undefined || data.price < 0) {
      errors.push('מחיר לא יכול להיות שלילי');
    }

    if (!data.category) {
      errors.push('אנא בחר קטגוריה');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static isValidCategory(category, availableCategories = []) {
    return availableCategories.some(cat => cat.value === category);
  }

  // Instance methods
  update(data) {
    Object.keys(data).forEach(key => {
      if (key !== 'id' && key !== 'created_at') {
        this[key] = data[key];
      }
    });
    this.updated_at = new Date().toISOString();
  }

  toggleActive() {
    this.is_active = !this.is_active;
    this.updated_at = new Date().toISOString();
  }

  getFormattedPrice() {
    return `$${this.price.toFixed(2)}`;
  }

  isVisibleToCustomers() {
    return this.is_visible_to_customers;
  }

  shouldShowPriceToCustomers() {
    return this.show_price_to_customers;
  }

  isAvailableForCustomerBooking() {
    return this.is_active && this.is_visible_to_customers;
  }

  static getServicesWithNotesOnCard() {
    return [
      'לו"ז סגור',
    ];
  }

  // Services that should not show payment information (e.g., breaks, blocked time)
  static getServicesWithoutPayment() {
    return [
      'לו"ז סגור',
    ];
  }

  getFormattedDuration() {
    const hours = Math.floor(this.duration / 60);
    const minutes = this.duration % 60;

    if (hours === 0) {
      return `${minutes} minutes`;
    } else if (minutes === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minutes`;
    }
  }

  toJSON() {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      duration: this.duration,
      price: this.price,
      color: this.color,
      category: this.category,
      is_active: this.is_active,
      is_visible_to_customers: this.is_visible_to_customers,
      show_price_to_customers: this.show_price_to_customers,
      created_at: this.created_at,
      updated_at: this.updated_at
    };
  }

  // Static methods for data operations
  static fromJSON(json) {
    return new Service(json);
  }

  static getCategories(categories = [], t = null) {
    return categories.map(category => ({
      ...category,
      label: category.label
    }));
  }

  static getCategoriesForSelect(categories = [], t = null) {
    return this.getCategories(categories, t).map(cat => ({
      value: cat.value,
      label: cat.label
    }));
  }

  static getCategoriesWithAll(categories = [], t = null) {
    return [
      { value: 'all', label: t?.allServices || 'All Services', icon: '🔹' },
      ...this.getCategories(categories, t)
    ];
  }

  static getCategoryColors(categories = []) {
    const colors = {};
    categories.forEach(cat => colors[cat.value] = cat.color);
    return colors;
  }

  static getCategoryColor(category, categories = []) {
    const cat = categories.find(c => c.value === category);
    return cat?.color || 'default';
  }

  static getCategoryIcon(category, categories = []) {
    const cat = categories.find(c => c.value === category);
    return cat?.icon || '🔹';
  }

  static getCategoryLabel(category, categories = []) {
    const cat = categories.find(c => c.value === category);
    return cat?.label || category;
  }
}